import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {CpConfigChildTab} from '../../../../../interface/cp-config-child-tab.interface';
import {CpSelectedAssignment} from '../../../../../interface/cp-selected-assignment.interface';
import {buildCpWidgetForm, CpWidgetColumn} from '../../../../../service/domain/cp-widget-column';
import {
    CP_CONDITION_SET_FIELD_ID,
    CP_CONDITION_SET_JOIN_AS,
    CP_UNCONDITIONAL_LABEL,
    cpRowValue
} from '../../../../../service/domain/cp-provision-assignment-columns';

/**
 * Generic child list of the ProvisionType object component — one instance per plain child
 * navigation point (Gruppen, Provisionsbasen, Provisionsarten, the six assignment tables,
 * Buchungsereignisse).
 *
 * All of these lists are structurally identical: a table filtered by the parent FK plus an FK
 * pre-filled create action. They are therefore driven by the {@link CpConfigChildTab} descriptor
 * declared in `CpProvisionTypeBaseComponent.childTabs` rather than by near-identical components.
 * Content-wise this is exactly what
 * `features/feature-bm/bd/component/object/dunning-type-object/dunning-type-object.component.ts`
 * does with its `createChildWidgets()` blocks; only the repetition is factored out.
 *
 * FK PRE-FILL (CONFIG-012): on create the parent FK is set AND the field is rendered read-only, so
 * the "ProvisionType… => FK provisionType" prefix rule holds in the UI itself. The
 * `DtoImportObjectContext` makes the create form resolve the parent — and it is also what lets the
 * backend form of an assignment restrict its "Bedingungssatz" picker to the sets of this type.
 *
 * ## Two build paths
 * - `defaultFormFields` — `createWidgetTableEntityFkeyResolveQl` narrowed by an id whitelist (see
 *   {@link CpConfigChildTab.defaultFormFields}); used by the plain tables.
 * - `qlQuery` + `widgetColumns` — `createWidgetTableQl` over a hand-written query with EXPLICIT
 *   joins and a curated `widgetForm`; used by all six assignment tables (condition-set join
 *   `pacs`, `cp-provision-assignment-columns.ts`) and by K-17's two-hop agent joins. The
 *   descriptor's query carries NO filters; the parent FK filter and `additionalFilters` are merged
 *   in here.
 *
 * ## Condition-set preview (assignment tables only)
 * With {@link CpConfigChildTab.conditionSetPreview} a row selection shows the conditions of the
 * row's set read-only below the table, and "Im Bedingungssatz öffnen" bubbles up through
 * {@link onJumpToConditionSet} to the object view, which switches to the navItem
 * "Bedingungssätze" with that set selected. The table itself is NOT rebuilt on selection.
 */
@Component({
    selector: 'cp-provision-type-child-table',
    templateUrl: './cp-provision-type-child-table.component.html',
    styleUrls: ['./cp-provision-type-child-table.component.scss'],
    standalone: false
})
export class CpProvisionTypeChildTableComponent implements OnInit, OnChanges {

    /** fieldId of the "Bedingungssatz" custom column template (`#cust_conditionSet`). */
    readonly conditionSetFieldId = CP_CONDITION_SET_FIELD_ID;

    /** Text of an empty condition set. */
    readonly unconditionalLabel = CP_UNCONDITIONAL_LABEL;

    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Descriptor of the child list to render — alias, entity and the two FK field names. */
    @Input() child: CpConfigChildTab;

    /**
     * Extra fixed filters ANDed onto the parent FK filter, e.g. the commission-component
     * pre-filter above the Matrix-Zuordnung table (04-frontend.md §6, "Vorfilter auf die gewählte
     * Provisionsart als Bedien-Hilfe"). `null`/empty = the unfiltered child list.
     *
     * The widget ALIAS stays the same across every value (CONFIG-011), so the user's column
     * widths and sorting survive switching the pre-filter.
     */
    @Input() additionalFilters: FilterCriteria[];

    /** "Im Bedingungssatz öffnen" of the preview — the set id to show in the navItem "Bedingungssätze". */
    @Output() onJumpToConditionSet = new EventEmitter<number>();

    childWidget: WidgetData;
    importContext: DtoImportObjectContext;

    /** Selected assignment row whose condition set is previewed; `null` = nothing selected. */
    selectedAssignment: CpSelectedAssignment | null = null;

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.selectedAssignment = null;

        if (!this.provisionTypeId || !this.child) {
            this.childWidget = null;
            this.importContext = null;
            this.initialized = true;
            return;
        }

        this.buildWidget();
        this.initialized = true;
    }

    /** Build the table on the path the descriptor declares. */
    private buildWidget(): void {
        if (this.child.qlQuery) {
            this.buildChildWidgetFromQuery();
        } else {
            this.buildChildWidget(this.child.defaultFormFields);
        }
    }

    /**
     * Build the parent-filtered EXPLICIT-JOIN table.
     *
     * The descriptor's query is cloned and the parent FK filter plus any `additionalFilters` are
     * merged in, so the descriptor itself stays a static, filter-free declaration. Columns come
     * from a curated `widgetForm` — `defaultFormFields` matching plays no part on this path.
     */
    private buildChildWidgetFromQuery(): void {

        const columns: CpWidgetColumn[] = [
            ...(this.child.widgetColumns ?? []),
            ...(this.child.widgetColumnsTrailing ?? [])
        ];

        const qlQuery: QlQueryDto = {
            ...this.child.qlQuery,
            fixedFilters: [
                ...(this.child.qlQuery.fixedFilters ?? []),
                FilterCriteria.create(this.child.fkFilterField, FilterCriteria.cOperatorEqual, this.provisionTypeId),
                ...(this.additionalFilters ?? [])
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Child table of the provision type for descriptors that declare their own QL
         *     query — the six assignment families (cp.ProvisionTypeContractEligibility,
         *     ProvisionTypeContractProvisionBase, ProvisionTypeContractLiability,
         *     ProvisionTypeMatrixAssignment, ProvisionTypeCalculatedAgent,
         *     ProvisionTypeComponentEligibility). Shows the rules of EXACTLY this provision type,
         *     optionally narrowed by the component pre-filter above the table, with the fixed column
         *     block Name, Alias, Bedingungssatz, Priorität, family columns, Gültigkeit, Status and
         *     result. Explicit joins (condition set `pacs`, K-17's two-hop agent names) instead of
         *     resolve_fkey_texts, curated through a widgetForm with German headers. A new row gets
         *     its mandatory FK provisionType pre-filled; a selected row previews its conditions.
         */
        this.childWidget = WidgetFactory.createWidgetTableQl(
            this.child.alias,
            this.child.label,
            qlQuery,
            WidgetDataParam.create('widgetForm', buildCpWidgetForm(columns))
        );

        if (this.child.conditionSetPreview) {
            // Row click selects the row (and feeds the preview) instead of doing nothing.
            this.childWidget.setParamValue('selectionMode', 'rowSelect');
        }

        this.applyCreateDefaults();
    }

    /**
     * Build the parent-filtered table plus its FK pre-filled create action.
     *
     * @param defaultFormFields curated column whitelist, or `undefined`/empty for the framework
     *                          default (every business column of the entity)
     */
    private buildChildWidget(defaultFormFields?: string[]): void {

        const params: WidgetDataParam[] = [];
        if (defaultFormFields && defaultFormFields.length) {
            params.push(WidgetDataParam.create('defaultFormFields', defaultFormFields));
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Child table of the provision type, filtered on the parent record via the
         *     mandatory FK provisionType. Entity and alias come from the CpConfigChildTab
         *     descriptor (cp.ProvisionTypeGroup, ProvisionTypeProvisionBase,
         *     ProvisionTypeComponent, ProvisionTypeTransactionEvent). The caseworker maintains the
         *     settings of EXACTLY this provision type here.
         */
        this.childWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            this.child.alias,
            this.child.label,
            this.child.entity,
            [
                FilterCriteria.create(this.child.fkFilterField, FilterCriteria.cOperatorEqual, this.provisionTypeId),
                ...(this.additionalFilters ?? [])
            ],
            [new Sorting('e.id', false)],
            ...params
        );

        this.applyCreateDefaults();
    }

    /** FK pre-fill of the create action plus the parent import context (CONFIG-012). */
    private applyCreateDefaults(): void {

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: this.child.fkCreateField, fieldValue: this.provisionTypeId}
        ];
        this.childWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.importContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId)]);
    }

    // --- condition-set column + preview ------------------------------------------------------- //

    /** Name of the condition set of one table row; `null` = unconditional. */
    conditionSetName(object: any): string | null {
        const name = cpRowValue(object, CP_CONDITION_SET_JOIN_AS + '.name');
        return typeof name === 'string' && name.trim().length ? name : null;
    }

    /** Alias of the condition set of one table row. */
    conditionSetAlias(object: any): string | null {
        const alias = cpRowValue(object, CP_CONDITION_SET_JOIN_AS + '.alias');
        return typeof alias === 'string' && alias.trim().length ? alias : null;
    }

    /**
     * Row selection of an assignment table — remembers the row so the preview below the table
     * shows its conditions. Non-assignment tables do not bind the event at all.
     */
    onRowSelect(event: ObjectIdentifier | ObjectIdentifierData): void {

        if (!this.child?.conditionSetPreview || !event) {
            return;
        }

        const data = (event as ObjectIdentifierData).data;
        const assignmentId = event.objectId ? +event.objectId : +cpRowValue(data, 'e.id');
        if (!assignmentId) {
            return;
        }

        const rawSetId = cpRowValue(data, CP_CONDITION_SET_JOIN_AS + '.id')
            ?? cpRowValue(data, 'e.provisionAssignmentConditionSetId');

        this.selectedAssignment = {
            assignmentId: assignmentId,
            name: cpRowValue(data, 'e.name') ?? '',
            conditionSetId: rawSetId !== null && rawSetId !== undefined && rawSetId !== '' ? +rawSetId : null,
            conditionSetName: this.conditionSetName(data),
            conditionSetAlias: this.conditionSetAlias(data)
        };
    }

    /** Close the preview. */
    onPreviewClose(): void {
        this.selectedAssignment = null;
    }

    /** Forward "Im Bedingungssatz öffnen" to the object view. */
    onPreviewJump(conditionSetId: number): void {
        if (conditionSetId) {
            this.onJumpToConditionSet.emit(conditionSetId);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId'] || changes['child']) {
            this.refreshComponent();
            return;
        }
        // A changed pre-filter only re-queries on the same path; the preview is dropped because
        // the selected row may no longer be in the list.
        if (changes['additionalFilters'] && this.childWidget) {
            this.selectedAssignment = null;
            this.buildWidget();
        }
    }

}
